/**
 * Support form controller for the /help/contact page.
 *
 * WHAT THIS FILE DOES:
 * - Renders the authenticated support form UI.
 * - Handles text input, attachments, voice recording, and submission.
 * - Manages UI state transitions (form → success).
 *
 * WHY THIS EXISTS:
 * - Keeps the main help-contact entrypoint focused on auth + bootstrapping.
 * - Encapsulates complex form behavior in a single, testable module.
 * - Avoids a "god module" by splitting the help bundle into focused parts.
 *
 * ARCHITECTURE CONTEXT:
 * - Used by packages/backend/src/client/help-contact.ts after Firebase auth.
 * - Submits to /api/support-messages (see backend support-messages routes).
 * - Matches the extension support modal UX for consistency.
 *
 * STATE MODEL:
 * - attachments: File[] selected by the user.
 * - audioBlob/audioUrl/audioDurationMs: voice note payload.
 * - isRecording/isUploading: UX gating for controls.
 *
 * DATA FLOW:
 * 1) User enters text / attachments / voice.
 * 2) Controller validates and enables Send.
 * 3) On send, Firebase ID token is used to POST to backend API.
 * 4) Success UI replaces form until reset.
 *
 * RELATED FILES:
 * - packages/backend/src/client/help-contact.ts (auth + boot)
 * - packages/backend/src/client/help-contact/support-message-api.ts (network layer)
 * - packages/backend/src/client/help-contact/debug-toggle-controller.ts (extension diagnostics UI)
 * - packages/backend/public/static/help-contact.css (styles)
 * - packages/chrome-plugin/extension/ui/options/support-message-modal.js (reference)
 *
 * NON-GOALS:
 * - No auth logic (handled by help-contact.ts).
 * - No extension debug toggle internals (handled by DebugToggleController).
 *
 * FILE HEADER SIZE TARGET:
 * - Keep this header between 50-120 lines for LLM readability.
 */

import type { User } from 'firebase/auth';
import { resolveUserEmail, resolveUserEmailWithReload } from '@gist-protocol/core';
import { submitSupportMessage, UploadErrors } from './support-message-api.js';
import {
  AttachmentAcceptValue,
  Limits,
  PlaybackIcons,
  RecordingErrors,
  RecordingFormats,
  TimeConstants
} from './constants.js';
import { createElement, createSvgIcon, createHeartIcon, createMicIcon, createPlayIcon, createPauseIcon, createStopIcon, createCloseIcon, createRefreshIcon } from './dom.js';
import { formatBytes, resolveAttachmentTypeLabel, validateAttachment } from './attachments.js';
import { DebugToggleController } from './debug-toggle-controller.js';

interface SupportFormControllerOptions {
  root: HTMLElement;
  /** Firebase user when the tab has a web session. */
  user?: User | null;
  /** Extension worker token when signed in via the extension only. */
  workerToken?: string | null;
  /** Email from extension auth or Firebase (for header copy). */
  displayEmail?: string | null;
  onSignOut: () => Promise<void> | void;
}

/**
 * Controller for rendering and managing the authenticated support form.
 */
export class SupportFormController {
  /** Root mount element for the help experience. */
  private root: HTMLElement;
  /** Firebase user used for ID token access (optional when extension-only). */
  private user: User | null;
  /** Extension worker session token (preferred when present). */
  private workerToken: string | null;
  /** Resolved email for UI copy. */
  private displayEmail: string | null;
  /** Sign-out handler provided by the parent controller. */
  private onSignOut: () => Promise<void> | void;
  /** Files selected for attachment upload. */
  private attachments: File[] = [];
  /** Recorded audio blob for the voice note, if any. */
  private audioBlob: Blob | null = null;
  /** Object URL for the recorded audio blob. */
  private audioUrl: string | null = null;
  /** Duration of the recorded audio in milliseconds. */
  private audioDurationMs: number | null = null;
  /** File extension for the recorded audio (webm/mp4). */
  private audioExtension: string = 'webm';
  /** MediaRecorder instance for capturing audio. */
  private mediaRecorder: MediaRecorder | null = null;
  /** MediaStream associated with the current recording session. */
  private mediaStream: MediaStream | null = null;
  /** Captured audio chunks from MediaRecorder. */
  private recordedChunks: Blob[] = [];
  /** Timer ID for updating the recording clock. */
  private recordingTimerId: ReturnType<typeof setInterval> | null = null;
  /** Timeout ID for auto-stopping at the max duration. */
  private recordingStopTimeoutId: ReturnType<typeof setTimeout> | null = null;
  /** Recording start timestamp (ms). */
  private recordingStartMs: number | null = null;
  /** True while a voice note is actively recording. */
  private isRecording: boolean = false;
  /** True while requesting microphone permission. */
  private isRequestingMic: boolean = false;
  /** True while submitting a support message. */
  private isUploading: boolean = false;
  /** Audio element used for playback controls. */
  private audioElement: HTMLAudioElement | null = null;
  /** Current playback icon state. */
  private playbackIconState: string = PlaybackIcons.PLAY;
  /** Debug toggle controller for extension diagnostics. */
  private debugToggle: DebugToggleController | null = null;

  constructor(options: SupportFormControllerOptions) {
    this.root = options.root;
    this.user = options.user ?? null;
    this.workerToken = options.workerToken ?? null;
    this.displayEmail = options.displayEmail ?? null;
    this.onSignOut = options.onSignOut;
  }

  private resolveDisplayEmail(): string {
    if (this.displayEmail) {
      return this.displayEmail;
    }
    if (this.user) {
      return resolveUserEmail(this.user) ?? 'your account email';
    }
    return 'your account email';
  }

  private async getBearerToken(): Promise<string> {
    if (this.workerToken) {
      return this.workerToken;
    }
    if (this.user) {
      return await this.user.getIdToken();
    }
    throw new Error(UploadErrors.UNAUTHENTICATED);
  }

  /**
   * Render the authenticated support form UI.
   */
  render(): void {
    this.debugToggle?.dispose();
    this.debugToggle = null;

    // Resolve across every provider (email/password, Google, Apple, custom token).
    // If the cached User hasn't hydrated yet, `hydrateEmailDisplay()` below will
    // patch the rendered text once `user.reload()` returns.
    const initialEmail = this.resolveDisplayEmail();
    const page = createElement('div', { class: 'help-page' });

    const header = createElement('header', { class: 'help-page__header help-page__header--compact' });
    const userDiv = createElement('div', { class: 'help-page__user' });
    userDiv.appendChild(createElement('span', { id: 'help-header-email', class: 'help-page__email' }, initialEmail));
    const signoutBtn = createElement('button', { id: 'help-signout-btn', type: 'button', class: 'help-page__signout' }, 'Sign out');
    userDiv.appendChild(signoutBtn);
    header.appendChild(userDiv);
    page.appendChild(header);

    const card = createElement('div', { class: 'help-form-card' });

    const cardHeader = createElement('div', { class: 'help-form-card__header' });
    const cardIcon = createElement('div', { class: 'help-form-card__icon' });
    cardIcon.appendChild(createHeartIcon());
    cardHeader.appendChild(cardIcon);

    const titleDiv = createElement('div', {});
    titleDiv.appendChild(createElement('h1', { class: 'help-form-card__title' }, "We're here to help"));
    titleDiv.appendChild(createElement('p', { class: 'help-form-card__subtitle' }, 'Tell us what is going on.'));
    cardHeader.appendChild(titleDiv);
    card.appendChild(cardHeader);

    this.debugToggle = new DebugToggleController({
      onError: (message) => this.showError(message)
    });
    card.appendChild(this.debugToggle.render());

    const errorEl = createElement('p', { id: 'help-form-error', class: 'help-form-error', role: 'alert', hidden: true });
    card.appendChild(errorEl);

    const formContent = createElement('div', { id: 'help-form-content', class: 'help-form-content' });
    formContent.appendChild(createElement('p', { class: 'help-form-description' }, 'Tell us what is happening. The more detail, the better.'));

    const messageDiv = createElement('div', { class: 'help-message' });
    const textarea = createElement('textarea', {
      id: 'help-message-text',
      placeholder: 'Describe what you expected, what happened instead, and any steps to reproduce.',
      maxlength: Limits.MAX_TEXT_CHARS
    });
    messageDiv.appendChild(textarea);

    const charCountDiv = createElement('div', { class: 'help-char-count' });
    charCountDiv.appendChild(createElement('span', { id: 'help-char-count' }, '0'));
    charCountDiv.appendChild(document.createTextNode(` / ${Limits.MAX_TEXT_CHARS.toLocaleString()}`));
    messageDiv.appendChild(charCountDiv);
    formContent.appendChild(messageDiv);

    const attachments = createElement('div', { class: 'help-attachments' });
    attachments.appendChild(this.buildVoiceBlock());
    attachments.appendChild(this.buildAttachmentBlock());
    formContent.appendChild(attachments);

    const sendBtn = createElement('button', { id: 'help-send-button', class: 'help-submit', type: 'button', disabled: true });
    const sendLabel = createElement('span', { id: 'help-send-label' }, 'Send message');
    sendBtn.appendChild(sendLabel);
    sendBtn.appendChild(this.createSendIcon());
    formContent.appendChild(sendBtn);
    card.appendChild(formContent);

    const successDiv = createElement('div', { id: 'help-form-success', class: 'help-success', hidden: true });

    const successIcon = createElement('div', { class: 'help-success__icon' });
    const checkSvg = createSvgIcon('0 0 24 24', ['M22 11.08V12a10 10 0 1 1-5.93-9.14', 'M22 4 12 14.01 9 11.01']);
    successIcon.appendChild(checkSvg);
    successDiv.appendChild(successIcon);

    successDiv.appendChild(createElement('h3', {}, 'Got it. We are on it.'));

    const successText = createElement('p', {});
    successText.appendChild(document.createTextNode('Thanks for taking the time to share this. We will follow up at '));
    successText.appendChild(createElement('strong', { id: 'help-success-email' }, initialEmail));
    successText.appendChild(document.createTextNode('.'));
    successDiv.appendChild(successText);

    successDiv.appendChild(createElement('button', { id: 'help-success-reset', class: 'help-submit help-submit--ghost', type: 'button' }, 'Send another message'));
    card.appendChild(successDiv);

    page.appendChild(card);
    this.root.replaceChildren(page);

    this.bindFormEvents();
    this.updateVoiceLayout();
    this.updateSendState();

    // If the cached User didn't have an email on any provider, reload once
    // and patch the rendered text. Fire-and-forget — render() stays sync.
    if (this.user && !this.displayEmail && !resolveUserEmail(this.user)) {
      void this.hydrateEmailDisplay();
    }
  }

  /**
   * Re-fetch the Firebase user and update any rendered email nodes.
   *
   * Called when the initial render fell back to "your account email" because
   * neither `user.email` nor any `providerData[*].email` was populated. One
   * `user.reload()` is usually enough to hydrate federated-provider data.
   */
  private async hydrateEmailDisplay(): Promise<void> {
    if (!this.user) {
      return;
    }
    const resolved = await resolveUserEmailWithReload(this.user);
    if (!resolved) return;
    const headerNode = document.getElementById('help-header-email');
    if (headerNode) headerNode.textContent = resolved;
    const successNode = document.getElementById('help-success-email');
    if (successNode) successNode.textContent = resolved;
  }

  /**
   * Dispose of any media resources when leaving the form view.
   */
  dispose(): void {
    this.stopRecordingTimer();
    this.cleanupRecordingResources();
    this.clearAudioState();
    this.debugToggle?.dispose();
    this.debugToggle = null;
  }

  private buildVoiceBlock(): HTMLElement {
    const voiceBlock = createElement('div', { class: 'help-voice-block' });
    const voicePrompt = createElement('div', { id: 'help-voice-prompt', class: 'help-voice-prompt' });

    const recordBtn = createElement('button', { id: 'help-voice-record-btn', class: 'help-record-btn', type: 'button', 'aria-label': 'Record voice note' });
    recordBtn.appendChild(createMicIcon());
    voicePrompt.appendChild(recordBtn);
    voicePrompt.appendChild(createElement('p', { class: 'help-record-hint' }, 'Send Matt a voice note'));
    voicePrompt.appendChild(createElement('p', { class: 'help-record-limit' }, 'Up to 10 minutes'));
    voiceBlock.appendChild(voicePrompt);

    const voiceRecorder = createElement('div', { id: 'help-voice-recorder', class: 'help-voice-recorder', hidden: true });
    const recorderActive = createElement('div', { class: 'help-recorder-active' });
    const recordingIndicator = createElement('div', { class: 'help-recording-indicator' });
    recordingIndicator.appendChild(createElement('span', { class: 'help-rec-dot' }));
    recordingIndicator.appendChild(createElement('span', { class: 'help-rec-label' }, 'Recording'));
    recorderActive.appendChild(recordingIndicator);
    recorderActive.appendChild(createElement('div', { id: 'help-recording-timer', class: 'help-recording-timer' }, '0:00'));

    const waveform = createElement('div', { class: 'help-waveform' });
    for (let i = 0; i < 9; i += 1) {
      waveform.appendChild(createElement('span', { class: 'help-wave-bar' }));
    }
    recorderActive.appendChild(waveform);

    const stopBtn = createElement('button', { id: 'help-voice-stop-btn', class: 'help-stop-btn', type: 'button' });
    stopBtn.appendChild(createStopIcon());
    stopBtn.appendChild(document.createTextNode('Stop recording'));
    recorderActive.appendChild(stopBtn);

    voiceRecorder.appendChild(recorderActive);
    voiceBlock.appendChild(voiceRecorder);

    const voicePlayback = createElement('div', { id: 'help-voice-playback', class: 'help-voice-playback', hidden: true });
    const playbackHeader = createElement('div', { class: 'help-playback-header' });
    playbackHeader.appendChild(createMicIcon());
    playbackHeader.appendChild(createElement('span', {}, 'Voice note ready'));
    const removeVoice = createElement('button', { id: 'help-voice-remove', class: 'help-remove-voice', type: 'button', 'aria-label': 'Remove voice note' });
    removeVoice.appendChild(createCloseIcon());
    playbackHeader.appendChild(removeVoice);
    voicePlayback.appendChild(playbackHeader);

    const playbackControls = createElement('div', { class: 'help-playback-controls' });
    const playBtn = createElement('button', { id: 'help-voice-play-btn', class: 'help-play-btn', type: 'button', 'aria-label': 'Play voice note' });
    playBtn.appendChild(createPlayIcon());
    playbackControls.appendChild(playBtn);

    const playbackProgress = createElement('div', { class: 'help-playback-progress' });
    const progressBar = createElement('div', { class: 'help-progress-bar' });
    progressBar.appendChild(createElement('div', { id: 'help-progress-fill', class: 'help-progress-fill' }));
    playbackProgress.appendChild(progressBar);

    const progressTime = createElement('div', { class: 'help-progress-time' });
    progressTime.appendChild(createElement('span', { id: 'help-progress-current' }, '0:00'));
    progressTime.appendChild(createElement('span', { id: 'help-progress-duration' }, '0:00'));
    playbackProgress.appendChild(progressTime);
    playbackControls.appendChild(playbackProgress);
    voicePlayback.appendChild(playbackControls);

    const rerecordBtn = createElement('button', { id: 'help-voice-rerecord', class: 'help-rerecord-btn', type: 'button' });
    rerecordBtn.appendChild(createRefreshIcon());
    rerecordBtn.appendChild(document.createTextNode('Record again'));
    voicePlayback.appendChild(rerecordBtn);

    const audioEl = createElement('audio', { id: 'help-voice-audio', preload: 'metadata' });
    audioEl.hidden = true;
    voicePlayback.appendChild(audioEl);

    voiceBlock.appendChild(voicePlayback);
    return voiceBlock;
  }

  private buildAttachmentBlock(): HTMLElement {
    const attachBlock = createElement('div', { class: 'help-attachment-block' });
    const attachHeader = createElement('div', { class: 'help-attachment-header' });

    const attachCopy = createElement('div', { class: 'help-attachment-copy' });
    attachCopy.appendChild(createElement('p', { class: 'help-attachment-title' }, 'Add screenshots, PDFs, or screen recordings'));
    attachCopy.appendChild(createElement('p', { class: 'help-attachment-note' }, 'JPEG, PNG, WebP, PDF, and supported video files up to 2 GB each.'));
    attachHeader.appendChild(attachCopy);

    attachHeader.appendChild(createElement('button', { id: 'help-attachments-button', class: 'help-attachment-button', type: 'button' }, 'Add attachments'));
    attachHeader.appendChild(createElement('input', { id: 'help-attachments-input', class: 'help-attachments-input', type: 'file', accept: AttachmentAcceptValue, multiple: true }));
    attachBlock.appendChild(attachHeader);

    attachBlock.appendChild(createElement('div', { id: 'help-attachments-list', class: 'help-attachments-list', hidden: true }));
    return attachBlock;
  }

  private createSendIcon(): SVGSVGElement {
    const sendIcon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    sendIcon.setAttribute('viewBox', '0 0 24 24');
    sendIcon.setAttribute('fill', 'none');
    sendIcon.setAttribute('stroke', 'currentColor');
    sendIcon.setAttribute('stroke-width', '2');
    sendIcon.setAttribute('aria-hidden', 'true');

    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line.setAttribute('x1', '22');
    line.setAttribute('y1', '2');
    line.setAttribute('x2', '11');
    line.setAttribute('y2', '13');
    sendIcon.appendChild(line);

    const polygon = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
    polygon.setAttribute('points', '22 2 15 22 11 13 2 9 22 2');
    sendIcon.appendChild(polygon);

    return sendIcon;
  }

  private bindFormEvents(): void {
    const signoutBtn = document.getElementById('help-signout-btn');
    signoutBtn?.addEventListener('click', async () => {
      this.resetFormState();
      await this.onSignOut();
    });

    const textarea = document.getElementById('help-message-text') as HTMLTextAreaElement | null;
    const charCount = document.getElementById('help-char-count');

    textarea?.addEventListener('input', () => {
      const len = textarea.value.length;
      if (charCount) {
        charCount.textContent = len.toLocaleString();
      }
      this.clearError();
      this.updateSendState();
    });

    const attachBtn = document.getElementById('help-attachments-button');
    const attachInput = document.getElementById('help-attachments-input') as HTMLInputElement | null;

    attachBtn?.addEventListener('click', () => {
      attachInput?.click();
    });

    attachInput?.addEventListener('change', () => {
      const files = attachInput.files;
      if (files && files.length > 0) {
        this.addAttachments(files);
        attachInput.value = '';
      }
    });

    const sendBtn = document.getElementById('help-send-button');
    sendBtn?.addEventListener('click', () => {
      void this.handleSend();
    });

    const resetBtn = document.getElementById('help-success-reset');
    resetBtn?.addEventListener('click', () => {
      this.resetFormState();
      this.render();
    });

    const recordBtn = document.getElementById('help-voice-record-btn');
    const stopBtn = document.getElementById('help-voice-stop-btn');
    const playBtn = document.getElementById('help-voice-play-btn');
    const removeBtn = document.getElementById('help-voice-remove');
    const rerecordBtn = document.getElementById('help-voice-rerecord');
    this.audioElement = document.getElementById('help-voice-audio') as HTMLAudioElement | null;

    recordBtn?.addEventListener('click', () => {
      void this.startRecording();
    });

    stopBtn?.addEventListener('click', () => {
      this.stopRecording();
    });

    playBtn?.addEventListener('click', () => {
      void this.togglePlayback();
    });

    removeBtn?.addEventListener('click', () => {
      this.clearAudioState();
      this.updateVoiceLayout();
      this.updateSendState();
    });

    rerecordBtn?.addEventListener('click', () => {
      this.clearAudioState();
      this.updateVoiceLayout();
      this.updateSendState();
    });

    this.audioElement?.addEventListener('loadedmetadata', () => {
      this.updatePlaybackProgress();
    });

    this.audioElement?.addEventListener('timeupdate', () => {
      this.updatePlaybackProgress();
    });

    this.audioElement?.addEventListener('ended', () => {
      this.setPlaybackState(false);
    });
  }

  private addAttachments(files: FileList): void {
    const next: File[] = [];
    for (const file of Array.from(files)) {
      const validation = validateAttachment(file);
      if (!validation.allowed) {
        if (validation.error) {
          this.showError(validation.error);
        }
        continue;
      }
      next.push(file);
    }

    if (next.length === 0) {
      return;
    }

    this.attachments = [...this.attachments, ...next];
    this.renderAttachmentsList();
    this.updateSendState();
  }

  private updateSendState(): void {
    const textarea = document.getElementById('help-message-text') as HTMLTextAreaElement | null;
    const sendBtn = document.getElementById('help-send-button') as HTMLButtonElement | null;
    if (!sendBtn) return;

    const hasText = (textarea?.value?.trim().length ?? 0) > 0;
    const hasAttachments = this.attachments.length > 0;
    const hasAudio = Boolean(this.audioBlob);
    const canSend = (hasText || hasAttachments || hasAudio) && !this.isUploading && !this.isRecording;

    sendBtn.disabled = !canSend;
  }

  private setUploadingState(isUploading: boolean): void {
    this.isUploading = isUploading;

    const textarea = document.getElementById('help-message-text') as HTMLTextAreaElement | null;
    const attachBtn = document.getElementById('help-attachments-button') as HTMLButtonElement | null;
    const attachInput = document.getElementById('help-attachments-input') as HTMLInputElement | null;
    const sendLabel = document.getElementById('help-send-label');

    if (textarea) textarea.disabled = isUploading;
    if (attachBtn) attachBtn.disabled = isUploading;
    if (attachInput) attachInput.disabled = isUploading;
    if (sendLabel) {
      sendLabel.textContent = isUploading ? 'Sending...' : 'Send message';
    }

    const removeButtons = document.querySelectorAll<HTMLButtonElement>('.help-attachment-remove');
    removeButtons.forEach((button) => {
      button.disabled = isUploading;
    });

    this.updateVoiceControls();
    this.updateSendState();
  }

  private async handleSend(): Promise<void> {
    if (this.isUploading) {
      return;
    }

    const textarea = document.getElementById('help-message-text') as HTMLTextAreaElement | null;
    const text = textarea?.value?.trim() ?? '';
    const hasAttachments = this.attachments.length > 0;
    const hasAudio = Boolean(this.audioBlob);

    if (!text && !hasAttachments && !hasAudio) {
      this.showError('Please add text, a voice note, or attachments.');
      return;
    }

    if (text.length > Limits.MAX_TEXT_CHARS) {
      this.showError('Your message is too long. Please keep it under 50,000 characters.');
      return;
    }

    if (hasAudio && this.audioDurationMs === null && this.audioElement && Number.isFinite(this.audioElement.duration)) {
      if (this.audioElement.duration > 0) {
        this.audioDurationMs = Math.round(this.audioElement.duration * TimeConstants.MS_PER_SECOND);
      }
    }

    if (hasAudio && this.audioDurationMs === null) {
      this.showError('Voice note duration is missing. Please re-record.');
      return;
    }

    this.clearError();
    this.setUploadingState(true);

    try {
      const bearerToken = await this.getBearerToken();
      await submitSupportMessage({
        bearerToken,
        text,
        audioBlob: this.audioBlob,
        audioFileName: this.audioBlob ? `support-message.${this.audioExtension}` : null,
        audioDurationMs: this.audioDurationMs,
        attachments: this.attachments
      });

      this.showSuccessState();
      this.resetDraftState();
    } catch (error) {
      const message = error instanceof Error ? error.message : UploadErrors.FAILED;
      this.showError(message);
    } finally {
      this.setUploadingState(false);
    }
  }

  private async startRecording(): Promise<void> {
    if (this.isRecording || this.isUploading || this.isRequestingMic) {
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      this.showError(RecordingErrors.UNSUPPORTED);
      return;
    }

    this.clearAudioState();
    this.clearError();

    const format = this.resolveRecordingFormat();
    this.audioExtension = format.extension;
    this.isRequestingMic = true;
    this.updateVoiceControls();

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      this.isRequestingMic = false;
      this.updateVoiceControls();
      this.showError(RecordingErrors.PERMISSION);
      return;
    }

    this.isRequestingMic = false;
    this.mediaStream = stream;

    try {
      this.mediaRecorder = new MediaRecorder(stream, format.mimeType ? { mimeType: format.mimeType } : undefined);
    } catch {
      this.cleanupRecordingResources();
      this.showError(RecordingErrors.UNSUPPORTED);
      return;
    }

    this.recordedChunks = [];
    this.mediaRecorder.addEventListener('dataavailable', (event) => {
      if (event.data && event.data.size > 0) {
        this.recordedChunks.push(event.data);
      }
    });

    this.mediaRecorder.addEventListener('stop', () => {
      this.finalizeRecording();
    });

    this.mediaRecorder.addEventListener('error', () => {
      this.showError(RecordingErrors.FAILED);
    });

    this.recordingStartMs = Date.now();
    this.isRecording = true;
    this.updateVoiceLayout();
    this.updateVoiceControls();

    this.startRecordingTimer();
    this.recordingStopTimeoutId = setTimeout(() => {
      this.stopRecording();
    }, Limits.MAX_RECORDING_MS);

    try {
      this.mediaRecorder.start();
    } catch {
      this.stopRecording();
      this.showError(RecordingErrors.FAILED);
    }
  }

  private stopRecording(): void {
    if (!this.mediaRecorder || !this.isRecording) {
      return;
    }

    try {
      this.mediaRecorder.stop();
    } catch {
      this.showError(RecordingErrors.FAILED);
    }

    this.isRecording = false;
    this.stopRecordingTimer();
    this.updateVoiceControls();
  }

  private finalizeRecording(): void {
    const durationMs = this.recordingStartMs ? Date.now() - this.recordingStartMs : null;
    this.recordingStartMs = null;
    this.stopRecordingTimer();

    const mimeType = this.mediaRecorder?.mimeType || 'audio/webm';
    const blob = new Blob(this.recordedChunks, { type: mimeType });
    this.recordedChunks = [];
    this.cleanupRecordingResources();

    if (!blob || blob.size === 0) {
      this.showError(RecordingErrors.FAILED);
      return;
    }

    if (blob.size > Limits.MAX_AUDIO_BYTES) {
      this.showError(RecordingErrors.TOO_LARGE);
      return;
    }

    this.audioBlob = blob;
    this.audioDurationMs = durationMs ? Math.min(durationMs, Limits.MAX_RECORDING_MS) : null;
    this.audioUrl = URL.createObjectURL(blob);

    if (this.audioElement) {
      this.audioElement.src = this.audioUrl;
      this.audioElement.load();
    }

    this.setPlaybackState(false);
    this.updateVoiceLayout();
    this.updateSendState();
  }

  private startRecordingTimer(): void {
    const timerEl = document.getElementById('help-recording-timer');
    if (timerEl) {
      timerEl.textContent = '0:00';
    }
    this.recordingTimerId = setInterval(() => {
      if (!this.recordingStartMs) return;
      const elapsedMs = Date.now() - this.recordingStartMs;
      const totalSeconds = Math.floor(elapsedMs / TimeConstants.MS_PER_SECOND);
      if (timerEl) {
        timerEl.textContent = this.formatTime(totalSeconds);
      }
    }, TimeConstants.TIMER_TICK_MS);
  }

  private stopRecordingTimer(): void {
    if (this.recordingTimerId) {
      clearInterval(this.recordingTimerId);
      this.recordingTimerId = null;
    }
    if (this.recordingStopTimeoutId) {
      clearTimeout(this.recordingStopTimeoutId);
      this.recordingStopTimeoutId = null;
    }
  }

  private updateVoiceLayout(): void {
    const prompt = document.getElementById('help-voice-prompt');
    const recorder = document.getElementById('help-voice-recorder');
    const playback = document.getElementById('help-voice-playback');

    if (this.isRecording) {
      if (prompt) prompt.hidden = true;
      if (recorder) recorder.hidden = false;
      if (playback) playback.hidden = true;
    } else if (this.audioBlob) {
      if (prompt) prompt.hidden = true;
      if (recorder) recorder.hidden = true;
      if (playback) playback.hidden = false;
    } else {
      if (prompt) prompt.hidden = false;
      if (recorder) recorder.hidden = true;
      if (playback) playback.hidden = true;
    }

    this.updateVoiceControls();
  }

  private updateVoiceControls(): void {
    const recordBtn = document.getElementById('help-voice-record-btn') as HTMLButtonElement | null;
    const stopBtn = document.getElementById('help-voice-stop-btn') as HTMLButtonElement | null;
    const playBtn = document.getElementById('help-voice-play-btn') as HTMLButtonElement | null;
    const removeBtn = document.getElementById('help-voice-remove') as HTMLButtonElement | null;
    const rerecordBtn = document.getElementById('help-voice-rerecord') as HTMLButtonElement | null;

    if (recordBtn) {
      recordBtn.disabled = this.isUploading || this.isRecording || this.isRequestingMic;
    }
    if (stopBtn) {
      stopBtn.disabled = this.isUploading || !this.isRecording;
    }
    if (playBtn) {
      playBtn.disabled = this.isUploading || !this.audioBlob;
    }
    if (removeBtn) {
      removeBtn.disabled = this.isUploading;
    }
    if (rerecordBtn) {
      rerecordBtn.disabled = this.isUploading;
    }
  }

  private async togglePlayback(): Promise<void> {
    if (!this.audioElement || !this.audioBlob) {
      return;
    }

    if (this.audioElement.paused) {
      try {
        await this.audioElement.play();
        this.setPlaybackState(true);
      } catch {
        this.showError('Unable to play the voice note. Please try again.');
      }
    } else {
      this.audioElement.pause();
      this.setPlaybackState(false);
    }
  }

  private setPlaybackState(isPlaying: boolean): void {
    const playBtn = document.getElementById('help-voice-play-btn');
    if (!playBtn) return;

    const nextState = isPlaying ? PlaybackIcons.PAUSE : PlaybackIcons.PLAY;
    if (this.playbackIconState === nextState) return;

    this.playbackIconState = nextState;
    playBtn.replaceChildren(isPlaying ? createPauseIcon() : createPlayIcon());
  }

  private updatePlaybackProgress(): void {
    if (!this.audioElement) return;
    const duration = Number.isFinite(this.audioElement.duration) ? this.audioElement.duration : 0;
    const current = this.audioElement.currentTime || 0;

    const fill = document.getElementById('help-progress-fill') as HTMLElement | null;
    const currentLabel = document.getElementById('help-progress-current');
    const durationLabel = document.getElementById('help-progress-duration');

    if (fill) {
      const percent = duration > 0 ? (current / duration) * 100 : 0;
      fill.style.width = `${Math.min(100, Math.max(0, percent))}%`;
    }

    if (currentLabel) {
      currentLabel.textContent = this.formatTime(Math.floor(current));
    }
    if (durationLabel) {
      durationLabel.textContent = this.formatTime(Math.floor(duration || 0));
    }

    if (this.audioDurationMs === null && duration && Number.isFinite(duration)) {
      this.audioDurationMs = Math.round(duration * TimeConstants.MS_PER_SECOND);
    }
  }

  private clearAudioState(): void {
    if (this.isRecording) {
      this.stopRecording();
    }

    if (this.audioElement) {
      this.audioElement.pause();
      this.audioElement.removeAttribute('src');
      this.audioElement.load();
    }

    if (this.audioUrl) {
      URL.revokeObjectURL(this.audioUrl);
    }

    this.audioBlob = null;
    this.audioUrl = null;
    this.audioDurationMs = null;
    this.playbackIconState = PlaybackIcons.PLAY;
  }

  private cleanupRecordingResources(): void {
    if (this.mediaRecorder) {
      this.mediaRecorder = null;
    }
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
      this.mediaStream = null;
    }
  }

  private resetDraftState(): void {
    const textarea = document.getElementById('help-message-text') as HTMLTextAreaElement | null;
    const charCount = document.getElementById('help-char-count');

    if (textarea) {
      textarea.value = '';
    }
    if (charCount) {
      charCount.textContent = '0';
    }

    this.attachments = [];
    this.renderAttachmentsList();
    this.clearAudioState();
    this.updateVoiceLayout();
    this.clearError();
    this.updateSendState();
  }

  private resetFormState(): void {
    this.stopRecordingTimer();
    this.cleanupRecordingResources();
    this.resetDraftState();

    const formContent = document.getElementById('help-form-content');
    const success = document.getElementById('help-form-success');
    if (formContent) formContent.hidden = false;
    if (success) success.hidden = true;
  }

  private showSuccessState(): void {
    const formContent = document.getElementById('help-form-content');
    const success = document.getElementById('help-form-success');
    if (formContent) formContent.hidden = true;
    if (success) success.hidden = false;
  }

  private showError(message: string): void {
    const errorEl = document.getElementById('help-form-error');
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.hidden = false;
    errorEl.classList.remove('help-form-error--info');
  }

  private clearError(): void {
    const errorEl = document.getElementById('help-form-error');
    if (!errorEl) return;
    errorEl.hidden = true;
    errorEl.classList.remove('help-form-error--info');
  }

  private resolveRecordingFormat(): { mimeType: string | null; extension: string } {
    if (typeof MediaRecorder === 'undefined') {
      return { mimeType: null, extension: 'webm' };
    }

    if (typeof MediaRecorder.isTypeSupported !== 'function') {
      return { mimeType: null, extension: 'webm' };
    }

    const match = RecordingFormats.find((format) => MediaRecorder.isTypeSupported(format.mimeType));
    if (!match) {
      return { mimeType: null, extension: 'webm' };
    }

    return { mimeType: match.mimeType, extension: match.extension };
  }

  private formatTime(totalSeconds: number): string {
    const minutes = Math.floor(totalSeconds / TimeConstants.SECONDS_PER_MINUTE);
    const seconds = totalSeconds % TimeConstants.SECONDS_PER_MINUTE;
    return `${minutes}:${String(seconds).padStart(TimeConstants.TIME_PAD_LENGTH, '0')}`;
  }

  private renderAttachmentsList(): void {
    const list = document.getElementById('help-attachments-list');
    if (!list) return;

    if (this.attachments.length === 0) {
      list.hidden = true;
      list.replaceChildren();
      return;
    }

    list.hidden = false;
    const items: HTMLElement[] = [];

    for (let i = 0; i < this.attachments.length; i += 1) {
      const file = this.attachments[i];
      const item = createElement('div', { class: 'help-attachment-item' });

      const meta = createElement('div', { class: 'help-attachment-meta' });
      meta.appendChild(createElement('div', { class: 'help-attachment-name' }, file.name));
      meta.appendChild(createElement('div', { class: 'help-attachment-details' }, `${resolveAttachmentTypeLabel(file)} - ${formatBytes(file.size)}`));
      item.appendChild(meta);

      const removeBtn = createElement('button', { class: 'help-attachment-remove', type: 'button' }, 'Remove');
      removeBtn.disabled = this.isUploading;
      const index = i;
      removeBtn.addEventListener('click', () => {
        this.attachments.splice(index, 1);
        this.renderAttachmentsList();
        this.updateSendState();
      });
      item.appendChild(removeBtn);

      items.push(item);
    }

    list.replaceChildren(...items);
  }
}
